import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { AddOnsCard } from './add-ons-card.component';
import { BillingScreenLayout } from './billing-screen-layout.component';

import {
  Company,
  cancelAddOnPurchase,
  extractBackendErrorMessage,
  postAddOnPurchase,
} from '@/services/hsm-backend';

export interface AddOnsFormProps {
  company: Company;
  refetch: () => Promise<void>;
}

interface AddOnsFormValues {
  messageQty: number | '';
}

const QUICK_PICKS = [25, 50, 100, 250, 500];

/**
 * "Comprar paquete" screen. Left card lists the company's add-on
 * purchases (with the Activos/Todos toggle); right card hosts the
 * form to POST a new add-on. Quick-pick chips let the admin set
 * common amounts in one click.
 *
 * On success: success toast, refetch the shell, reset the form. On
 * error: error toast with the backend message.
 *
 * @param company Loaded company from the shell's outlet context.
 * @param refetch Callback that retriggers the shell's getCompany.
 */
export const AddOnsForm = ({ company, refetch }: AddOnsFormProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const addOns = company.subscription?.addOnPurchases ?? [];

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddOnsFormValues>({
    defaultValues: { messageQty: '' },
  });

  // A quick-pick sets the value via `setValue` without focusing the input, so
  // MUI's auto "filled" detection misses it and the label overlaps the value.
  // Force the label to shrink whenever there is a value; leave it auto (label
  // as placeholder) when empty.
  const messageQty = watch('messageQty');

  const onSubmit = async (values: AddOnsFormValues) => {
    try {
      await postAddOnPurchase(company.companyId, {
        messageQty: Number(values.messageQty),
      });
      enqueueSnackbar('Paquete comprado', { variant: 'success' });
      await refetch();
      reset({ messageQty: '' });
    } catch (error) {
      enqueueSnackbar(
        `No se pudo comprar el paquete: ${extractBackendErrorMessage(error)}`,
        { variant: 'error' }
      );
    }
  };

  const handleQuickPick = (value: number) => {
    setValue('messageQty', value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <BillingScreenLayout
      title="Comprar paquete"
      subtitle="Mensajes extra que se suman al plan, no expiran al renovar el ciclo."
      currentCard={
        <AddOnsCard
          addOns={addOns}
          title="Paquetes activos"
          onCancelAddOn={(addOnId) =>
            cancelAddOnPurchase(company.companyId, addOnId)
          }
          onCancelled={refetch}
        />
      }
      formCard={
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box
              component="form"
              onSubmit={(e) => void handleSubmit(onSubmit)(e)}
              noValidate
            >
              <Typography variant="h6">Nuevo paquete</Typography>
              <Divider sx={{ mt: 1, mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Cantidad de mensajes"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1 }}
                  InputLabelProps={{
                    shrink: messageQty === '' ? undefined : true,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">mensajes</InputAdornment>
                    ),
                  }}
                  helperText={
                    errors.messageQty?.message ??
                    'Se agregan al saldo de paquetes de la empresa.'
                  }
                  error={Boolean(errors.messageQty)}
                  {...register('messageQty', {
                    required: 'Requerido',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Debe ser mayor a 0' },
                  })}
                />
                <Stack direction="row" spacing={1}>
                  {QUICK_PICKS.map((value) => (
                    <Chip
                      key={value}
                      label={value}
                      variant="outlined"
                      clickable
                      onClick={() => handleQuickPick(value)}
                    />
                  ))}
                </Stack>
              </Stack>
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1}
                sx={{ mt: 3 }}
              >
                <Button
                  variant="text"
                  onClick={() => navigate(`/company/${company.companyId}`)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  endIcon={<ArrowForwardIcon />}
                >
                  Comprar paquete
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      }
    />
  );
};
